import { NavLink, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useClientPerms } from "../lib/useClientPerms";
import { useBrand } from "../lib/useBrand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { AccountSecurityPanel } from "./AccountSecurity";
import {
  LayoutDashboard, Users, PlayCircle, History, BarChart3, FileText, Landmark, LogOut, Eye, Building2, Clock, UserCog, ShieldCheck, Upload,
  Bell, Wallet,
} from "lucide-react";

const NAV = [
  { to: "/client", label: "Dashboard", icon: LayoutDashboard, testid: "cnav-dashboard", end: true },
  { to: "/client/company", label: "Company Profile", icon: Building2, testid: "cnav-company", area: "company" },
  { to: "/client/time", label: "Time", icon: Clock, testid: "cnav-time", area: "time" },
  { to: "/client/people", label: "People", icon: UserCog, testid: "cnav-people", area: "people" },
  { to: "/client/migration", label: "Migration", icon: Upload, testid: "cnav-migration", area: "migration" },
  { to: "/client/run-payroll", label: "Run Payroll", icon: PlayCircle, testid: "cnav-run-payroll", area: "run_payroll" },
  { to: "/client/history", label: "Pay History", icon: History, testid: "cnav-history", area: "history" },
  { to: "/client/remittance", label: "Remittance", icon: Landmark, testid: "cnav-remittance", area: "remittance" },
  { to: "/client/reports", label: "Reports", icon: BarChart3, testid: "cnav-reports", area: "reports" },
  { to: "/client/t4", label: "T4 Slips", icon: FileText, testid: "cnav-t4", area: "t4" },
  { to: "/client/notifications", label: "Notifications", icon: Bell, testid: "cnav-notifications", area: "notifications" },
];

const ROLE_LABEL = { admin: "Administrator", preparer: "Payroll Preparer", mgt: "MGT (Management / Owner)", viewer: "Viewer" };

// Route → permission area. Sub-routes (e.g. /client/history/:runId) inherit their parent's area.
const ROUTE_AREAS = [
  ["/client/company", "company"], ["/client/employees", "employees"], ["/client/time", "time"], ["/client/people", "people"],
  ["/client/migration", "migration"], ["/client/run-payroll", "run_payroll"], ["/client/history", "history"],
  ["/client/remittance", "remittance"], ["/client/reports", "reports"], ["/client/t4", "t4"], ["/client/roe", "t4"], ["/client/notifications", "notifications"],
];
const areaForPath = (path) => ROUTE_AREAS.find(([p]) => path === p || path.startsWith(p + "/"))?.[1] || null;

export default function ClientLayout({ children }) {
  const { user, logout, isImpersonating, exitImpersonation } = useAuth();
  const { pathname } = useLocation();
  const role = user?.client_role || "admin";
  const brand = useBrand();
  const brandName = brand.name || "Postings Inc";
  const { loaded, canView } = useClientPerms();
  const [acctOpen, setAcctOpen] = useState(false);

  const [timeTab, setTimeTab] = useState(true);
  const [migClosed, setMigClosed] = useState(false);
  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/companies/${user.company_id}`).then((r) => { setTimeTab(r.data?.time_tab_enabled !== false); setMigClosed(!!r.data?.migration_closed); }).catch(() => {});
  }, [user?.company_id]);
  const nav = NAV.filter((n) => canView(n.area) && (n.area !== "time" || timeTab) && (n.area !== "migration" || !migClosed));
  const currentArea = areaForPath(pathname);
  const blocked = loaded && currentArea && !canView(currentArea);
  useEffect(() => {
    if (blocked) api.post("/client/access-denied", { area: currentArea, path: pathname }).catch(() => {});
  }, [blocked, currentArea, pathname]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white text-slate-800">
      {isImpersonating && (
        <div data-testid="impersonation-banner" className="bg-amber-400 text-amber-950 text-sm font-medium px-4 py-2 flex items-center justify-center gap-3">
          <Eye className="h-4 w-4" /> {user?.role_preview ? `Previewing portal as ${ROLE_LABEL[role]} (read-only)` : "Previewing client portal"}
          <button data-testid="exit-impersonation" onClick={user?.role_preview ? () => { localStorage.removeItem("impersonation_token"); window.location.href = "/settings"; } : exitImpersonation} className="ml-2 rounded-full bg-amber-950 text-amber-50 px-3 py-0.5 text-xs hover:bg-amber-900 transition-colors">Exit preview</button>
        </div>
      )}
      <div className="flex">
        <aside className="w-64 shrink-0 min-h-screen bg-white/70 backdrop-blur border-r border-slate-200 hidden md:flex flex-col">
          <div className="px-6 py-6 flex items-center gap-3">
            {brand.logo ? (
              <img src={brand.logo} alt={brandName} data-testid="client-brand-logo" className="h-9 w-auto max-w-[150px] object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-xl bg-sky-600 text-white flex items-center justify-center font-bold">P</div>
            )}
            <div>
              <div className="font-bold tracking-tight leading-none">Payroll Portal</div>
              <div className="text-[11px] text-slate-500 mt-0.5" data-testid="client-brand-name">by {brandName}</div>
            </div>
          </div>
          <nav className="flex-1 px-3 space-y-1">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-sky-600 text-white shadow-sm" : "text-slate-600 hover:bg-sky-100"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="px-4 py-4 border-t border-slate-200">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-500 mb-3">{ROLE_LABEL[role]}</div>
            {user?.linked_employee_id && (
              <Link to="/me" data-testid="client-my-pay-switch" className="flex items-center gap-2 text-sm text-sky-700 hover:text-sky-900 transition-colors mb-2.5 font-semibold">
                <Wallet className="h-4 w-4" /> My pay (employee view)
              </Link>
            )}
            <button data-testid="client-account-button" onClick={() => setAcctOpen(true)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-sky-700 transition-colors mb-2.5">
              <ShieldCheck className="h-4 w-4" /> Account & Security
            </button>
            <button data-testid="client-logout" onClick={logout} className="flex items-center gap-2 text-sm text-slate-600 hover:text-red-600 transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </aside>

        <Dialog open={acctOpen} onOpenChange={setAcctOpen}>
          <DialogContent data-testid="account-security-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle>Account &amp; Security</DialogTitle>
              <DialogDescription>Change your password or connect Google sign-in.</DialogDescription>
            </DialogHeader>
            <AccountSecurityPanel />
          </DialogContent>
        </Dialog>

        <div className="flex-1 min-w-0">
          <nav className="md:hidden flex overflow-x-auto gap-1 bg-white/80 border-b border-slate-200 px-2 py-2">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`m-${n.testid}`}
                className={({ isActive }) => `flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs whitespace-nowrap ${isActive ? "bg-sky-600 text-white" : "text-slate-600 bg-slate-100"}`}>
                <n.icon className="h-3.5 w-3.5" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <main className="p-6 lg:p-10 w-full">
            {blocked ? (
              <div data-testid="client-no-access" className="rounded-2xl border border-slate-200 bg-white p-10 text-center">
                <ShieldCheck className="h-8 w-8 mx-auto text-slate-400" />
                <h1 className="mt-4 text-xl font-bold tracking-tight">No access to this area</h1>
                <p className="mt-2 text-sm text-slate-500">Your role ({ROLE_LABEL[role]}) doesn't include this section. Ask your payroll provider if you need access.</p>
                <Link to="/client" data-testid="client-no-access-home" className="inline-block mt-6 rounded-full bg-sky-600 text-white px-5 py-2 text-sm font-medium hover:bg-sky-700 transition-colors">Back to dashboard</Link>
              </div>
            ) : (!loaded && currentArea) ? (
              <div className="font-mono text-xs uppercase tracking-widest text-slate-400 animate-pulse">Loading…</div>
            ) : children}
          </main>
        </div>
      </div>
    </div>
  );
}
